<template>
  <section
    class="flex flex-col"
    :class="embedInScrollParent ? undefined : 'flex-auto grow min-h-0'"
  >
    <div class="my-2 flex items-center gap-x-4 rounded-12 bg-bg-base-bg3 p-[10px]">
      <wui-icon
        name="search"
        class="shrink-0 text-fg-base-fg32"
      />
      <input
        v-model="contactModel"
        class="w-full bg-transparent text-body-r-14-22 text-fg-base-fg3 outline-none placeholder:text-fg-base-fg32"
        type="text"
        data-test="chat-participants-search"
        :placeholder="labelForInput"
      >
      <button
        v-if="contactModel"
        type="button"
        class="shrink-0 text-fg-base-fg32 hover:text-fg-base-fg17"
        data-test="chat-participants-search-clear"
        :aria-label="$t('common.clear')"
        @click="contactModel = ''"
      >
        <wui-icon name="clear" />
      </button>
    </div>
    <div
      class="relative flex w-full"
      :class="embedInScrollParent ? undefined : 'flex-auto grow min-h-0'"
    >
      <ul
        class="w-full min-w-0 grow"
        :class="embedInScrollParent ? undefined : 'min-h-0 overflow-y-auto hide-scrollbar'"
        data-test="chat-participants-list"
      >
        <li
          v-if="canShowAddParticipants"
          class="w-full"
        >
          <button
            type="button"
            class="flex w-full items-center gap-x-4 rounded-8 p-2 text-left hover:bg-bg-base-bg3"
            data-test="chat-participants-add"
            @click="showAddMembersModal"
          >
            <div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-bg-base-bg3">
              <wui-icon
                name="userAddM"
                class="text-surf-brand-base-def"
              />
            </div>
            <p class="text-body-r-14-20 text-surf-brand-base-def">
              {{ $t('calls.addParticipants') }}
            </p>
          </button>
        </li>
        <li
          v-if="waitingApprovePSTNGuests.size"
          class="mb-4"
        >
          <div class="border-b border-bor-nav pb-2 text-1316 text-fg-base-fg22">
            {{ $t('chat.waitingApproves') }}
          </div>
        </li>
        <li
          v-for="[guid, pstnGuest] in waitingApprovePSTNGuests"
          :key="guid"
          class="w-full"
          @click.right.prevent
        >
          <chat-participant-list-item
            :key="guid"
            :contact-id="pstnGuest.id"
            :room-id="room.pServedUser"
            :contact="pstnGuest as Contact"
            :need-approve="true"
            @click="onContactClick(pstnGuest as Contact)"
            @click.right.shift.stop
          />
        </li>
        <li
          v-if="waitingApprovePSTNGuests.size && (filteredContacts.size || filteredGuests.size)"
          class="my-4 border-t border-bor-nav"
        />
        <li
          v-for="[guid, contact] in filteredContacts"
          :key="guid"
          class="w-full"
        >
          <chat-participant-list-item
            :key="guid"
            :contact="contact!"
            :style="{ 'anchor-name': `--contact-${contact.id}` }"
            :contact-id="contact.id"
            :room-id="room.pServedUser"
            :is-menu-showed="activeContact?.id === contact.id && isMenuShowed"
            :role="getSubscriberRole(contact.imLogin)!"
            @click="onContactClick(contact)"
            @show-menu="showMenu(contact)"
            @open-chat="onOpenChat(contact)"
            @call="onCall(contact)"
          />
        </li>
        <li
          v-for="[key, guest] in filteredGuests"
          :key="key"
          v-touch:[guest.pServed].long="() => onGuestRightClick(guest)"
          class="w-full cursor-pointer rounded-8"
        >
          <chat-guest-list-item
            :guest="guest"
            :room-id="room.id"
            :anchor-name="getGuestAnchorName(guest.pServed)"
            :is-menu-showed="activeGuest?.pServed === guest.pServed && isGuestMenuShowed"
            @show-menu="onGuestRightClick(guest)"
          />
        </li>
        <li
          v-if="isSearchEmpty"
          class="px-4 py-10 text-center text-body-r-15-22 text-fg-base-fg17"
          data-test="chat-participants-search-empty"
        >
          {{ $t('common.nothingFound') }}
        </li>
      </ul>
      <div
        v-if="isMenuShowed"
        v-click-outside="hideMenu"
        class="absolute w-max"
        :style="contactDropdownStyle"
      >
        <chat-participant-context-menu
          v-if="activeContact"
          :contact="activeContact"
          :room="room"
          :is-removable="canIEditRoomInfo"
          @hide-menu="hideMenu"
        />
      </div>
      <div
        v-if="isGuestMenuShowed"
        v-click-outside="hideGuestMenu"
        class="absolute w-max"
        :style="guestDropdownStyle"
      >
        <guest-item-dropdown
          v-if="activeGuest"
          :guest="activeGuest"
          :room-id="room.id"
          :room-p-served="room.pServedUser"
          @hide-menu="hideGuestMenu"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ClickOutside as vClickOutside, WuiIcon, useDialog, useRenderNotifier } from '@wui/common-library'
import { isBotPServed } from '@wui/im'
import { computed, defineAsyncComponent, ref, toRef } from 'vue'
import { useRouter } from 'vue-router'


import { AddChatParticipantsModal } from '@/features/add-chat-participants'
import { ChatParticipantContextMenu } from '@/features/chat-participant-context-menu'
import { GuestItemDropdown } from '@/features/guest-item-dropdown'

import { Guest } from '@/entities/conference-participant'
import { ExternalContactCard, useStoreContacts } from '@/entities/contact'
import { useStoreWaitingApprove, useRoomMembersLabel, useModeratedRoom } from '@/entities/room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useLocalization } from '@/shared/composables/localization'
import { isMobileLayout } from '@/shared/composables/use-mobile'
import { openParticipantProfileOverlay } from '@/shared/composables/use-participant-profile-overlay'
import { useRoomControl } from '@/shared/composables/use-room-control'
import { useStoreContactStatus } from '@/shared/contact-status'
import { vTouch } from '@/shared/directives/v-touch'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { PSTNGUEST } from '@/shared/utils/constants'
import { optionalPush } from '@/shared/utils/router'
import sort, { byRole } from '@/shared/utils/sort'
import { getChatRoute } from '@/shared/utils/url'

const ChatParticipantListItem = defineAsyncComponent(() => import('@/entities/chat-participant').then(({ ChatParticipantListItem }) => ChatParticipantListItem))
const ChatGuestListItem = defineAsyncComponent(() => import('@/entities/chat-participant').then(({ ChatGuestListItem }) => ChatGuestListItem))

const props = defineProps<{
  room: Room
  embedInScrollParent?: boolean
}>()

const roomRef = toRef(props, 'room')
const { byOnline, byLastSeen } = sort.contacts
const { getStatusByPServed } = useStoreContactStatus()
const getContactStatus = (c: Contact) => getStatusByPServed(c.imLogin)
const { getWaitingApprovePSTNGuestsByRoomId } = useStoreWaitingApprove()
const { showDialog } = useDialog()
const { currentUser } = useCurrentUser()
const { currentGuest } = useGuestAuth()
const { label: labelForInput } = useRoomMembersLabel(props.room, false)
const { canIEditRoomInfo, getSubscriberRole } = useModeratedRoom(roomRef)

const isMenuShowed = ref(false)
const isGuestMenuShowed = ref(false)
const activeContact = ref<Contact>()
const activeGuest = ref<Guest>()

const waitingApprovePSTNGuests = computed(() => getWaitingApprovePSTNGuestsByRoomId(props.room.pServedUser))

const contactModel = ref('')
const filteredContacts = computed(() => {
  return new Map(
    [...props.room.subscribers].filter(([, contact]) => {
      return !waitingApprovePSTNGuests.value.has(contact.id) && !props.room.guests?.get(contact.id) && contact.name
        .toLowerCase()
        .includes(contactModel.value.toLowerCase())
    }).sort((a, b) => byLastSeen(a[1], b[1], getContactStatus))
      .sort((a, b) => byOnline(a[1], b[1], getContactStatus))
      .sort((a, b) => byRole(getSubscriberRole(a[1].imLogin), getSubscriberRole(b[1].imLogin)))
      .sort((a, b) => a[1].imLogin === currentUser.value?.imLogin ? -1 : b[1].imLogin === currentUser.value?.imLogin ? 1 : 0),
  )
})

const filteredGuests = computed(() => {
  if (!props.room.guests) return new Map()

  return new Map(
    [...props.room.guests].filter(([, guest]) => {
      return guest.name
        .toLowerCase()
        .includes(contactModel.value.toLowerCase())
    }),
  )
})

const canShowAddParticipants = computed(() => Boolean(canIEditRoomInfo.value) && !currentGuest.value)

const isSearchEmpty = computed(() => {
  return Boolean(contactModel.value.trim())
    && filteredContacts.value.size === 0
    && filteredGuests.value.size === 0
})

const showAddMembersModal = () => {
  showDialog(AddChatParticipantsModal, {
    id: 'AddChatParticipantsModal',
    room: props.room,
    hasOverlay: true,
  })
}

const { findByPServed, findById, findContactByMobilePhone } = useStoreContacts()
const router = useRouter()
const { memberCall } = useRoomControl()
const { notifyError } = useRenderNotifier()
const { t } = useLocalization()

const isSelfContact = (contact: Contact) => {
  return contact.id === currentUser.value?.id
    || Boolean(contact.imLogin && contact.imLogin === currentUser.value?.imLogin)
}

const isExternalContact = (contact: Contact) => {
  return contact.type === 'pstnGuest' || contact.type === 'guest' || !contact.imLogin
}

const getMemberCallNumber = (contact: Contact) => {
  if (isExternalContact(contact)) {
    const suffix = contact.internalNumber || contact.mobilePhone
    return suffix ? `${PSTNGUEST}-${suffix}` : ''
  }
  return contact.internalNumber || ''
}

const openContactProfile = (contact: Contact) => {
  openParticipantProfileOverlay(contact)
}

const openFavorites = async () => {
  if (!currentUser.value) {
    return
  }
  const chatRoute = getChatRoute(currentUser.value)
  if (!chatRoute) {
    return
  }
  await router.push(chatRoute)
}

const onContactClick = (contact: Contact): void => {
  if (currentGuest.value) {
    return
  }
  if (isSelfContact(contact)) {
    void openFavorites()
    return
  }
  if (isMobileLayout.value) {
    showMenu(contact)
    return
  }
  if (contact.imLogin && (isBotPServed(contact.imLogin) || findByPServed(contact.imLogin))) {
    openContactProfile(contact)
    return
  }
  const found = findContactByMobilePhone(contact.id)
  if (!contact.imLogin && contact.mobilePhone && (findById(contact.id) || found)) {
    showDialog(ExternalContactCard, {
      id: 'ExternalContactCard',
      contactId: found ? found.id : contact.id,
      readonly: true,
    })
  }
}

const onOpenChat = (contact: Contact) => {
  if (!contact.imLogin) {
    return
  }
  optionalPush(getChatRoute(contact))
}

const onCall = async (contact: Contact) => {
  const number = getMemberCallNumber(contact)
  if (!number) {
    return
  }
  try {
    await memberCall(props.room.pServedUser, number)
  } catch (e: unknown) {
    notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  }
}

const showMenu = (contact: Contact) => {
  if (isSelfContact(contact) || currentGuest.value) return
  isGuestMenuShowed.value = false
  activeContact.value = contact
  setTimeout(() => {
    isMenuShowed.value = true
  }, 0)
}

const contactDropdownStyle = computed(() => {
  if (!activeContact.value) return {}
  return {
    'position-anchor': `--contact-${activeContact.value.id}`,
    'position-area': 'bottom',
    'position-try-fallbacks': 'flip-block',
    'margin': '-20px',
  } as Record<string, string>
})

const getGuestAnchorName = (pServed: string) => {
  return `--guest-${pServed.replace(/[^a-zA-Z0-9_-]/g, '-')}`
}

const guestDropdownStyle = computed(() => {
  if (!activeGuest.value) return {}
  return {
    'position-anchor': getGuestAnchorName(activeGuest.value.pServed),
    'position-area': 'left',
    'position-try-fallbacks': 'flip-block',
    'margin': '10px',
  } as Record<string, string>
})

const onGuestRightClick = (guest: Guest) => {
  if (currentGuest.value) return
  isMenuShowed.value = false
  activeGuest.value = guest
  setTimeout(() => {
    isGuestMenuShowed.value = true
  }, 0)
}

const hideMenu = () => {
  isMenuShowed.value = false
}

const hideGuestMenu = () => {
  isGuestMenuShowed.value = false
}
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
</style>
